import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  Plus,
  ExternalLink,
  X,
  Code,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  deploymentsApi,
  servicesApi,
  environmentsApi,
  groupsApi,
} from "@/api/client";
import EnvironmentSelect from "@/components/EnvironmentSelect";
import { formatDate, titleCase } from "@/lib/utils";
import DeploymentDetailsModal from "@/components/DeploymentDetailsModal";

interface ComponentVersion {
  identifier: string;
  version: string;
}

export default function Deployments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [showApiExamples, setShowApiExamples] = useState(false);
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<
    string | null
  >(null);
  const [componentVersions, setComponentVersions] = useState<
    ComponentVersion[]
  >([]);
  const [filters, setFilters] = useState({
    service: "",
    environment: "",
    group: searchParams.get("group") || "",
  });

  const queryClient = useQueryClient();

  // Sync group filter with URL
  useEffect(() => {
    const urlGroup = searchParams.get("group") || "";
    if (urlGroup !== filters.group) {
      setFilters((prev) => ({ ...prev, group: urlGroup }));
    }
  }, [searchParams]);

  const { data: deployments, isLoading } = useQuery({
    queryKey: ["deployments", filters],
    queryFn: () =>
      deploymentsApi.list({
        service: filters.service || undefined,
        environment: filters.environment || undefined,
        group: filters.group || undefined,
        limit: 50,
      }),
  });

  const { data: services } = useQuery({
    queryKey: ["services"],
    queryFn: () => servicesApi.list(),
  });

  const { data: environments } = useQuery({
    queryKey: ["environments"],
    queryFn: () => environmentsApi.list(),
  });

  const { data: groupsData } = useQuery({
    queryKey: ["groups"],
    queryFn: () => groupsApi.list(),
  });

  const groups = groupsData?.groups || [];

  const createMutation = useMutation({
    mutationFn: deploymentsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deployments"] });
      queryClient.invalidateQueries({ queryKey: ["state"] });
      setShowForm(false);
      setComponentVersions([]);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Build component_versions object from state
    const componentVersionsObj: Record<string, string> = {};
    componentVersions.forEach((cv) => {
      if (cv.identifier && cv.version) {
        componentVersionsObj[cv.identifier] = cv.version;
      }
    });

    createMutation.mutate({
      service: formData.get("service") as string,
      version: formData.get("version") as string,
      environment: formData.get("environment") as string,
      deployed_by: formData.get("deployed_by") as string,
      deployment_method: "manual",
      notes: formData.get("notes") as string,
      component_versions:
        Object.keys(componentVersionsObj).length > 0
          ? componentVersionsObj
          : undefined,
    });
  };

  const addComponentVersion = () => {
    setComponentVersions([
      ...componentVersions,
      { identifier: "", version: "" },
    ]);
  };

  const updateComponentVersion = (
    index: number,
    field: "identifier" | "version",
    value: string,
  ) => {
    const updated = [...componentVersions];
    updated[index][field] = value;
    setComponentVersions(updated);
  };

  const removeComponentVersion = (index: number) => {
    setComponentVersions(componentVersions.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Deployments</h1>
          <p className="mt-1 text-sm text-gray-500">
            Track all deployments across your environments
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Record Deployment
        </button>
      </div>

      {/* Manual Entry Form */}
      {showForm && (
        <div className="mt-6 card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Record Manual Deployment
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="service" className="label">
                  Service
                </label>
                <select name="service" id="service" className="input" required>
                  <option value="">Select a service</option>
                  {(services?.services ?? []).map((svc) => (
                    <option key={svc.id} value={svc.slug}>
                      {svc.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="environment" className="label">
                  Environment
                </label>
                <select
                  name="environment"
                  id="environment"
                  className="input"
                  required
                >
                  <option value="">Select an environment</option>
                  {(environments?.environments ?? []).map((env) => (
                    <option key={env.id} value={env.slug}>
                      {env.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="version" className="label">
                  Version
                </label>
                <input
                  type="text"
                  name="version"
                  id="version"
                  className="input"
                  placeholder="1.2.3"
                  required
                />
              </div>
              <div>
                <label htmlFor="deployed_by" className="label">
                  Deployed By
                </label>
                <input
                  type="text"
                  name="deployed_by"
                  id="deployed_by"
                  className="input"
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="notes" className="label">
                Notes (optional)
              </label>
              <textarea
                name="notes"
                id="notes"
                rows={2}
                className="input"
                placeholder="Any notes about this deployment..."
              />
            </div>

            {/* Component Versions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label mb-0">
                  Component Versions (optional)
                </label>
                <button
                  type="button"
                  onClick={addComponentVersion}
                  className="text-sm text-brand-600 hover:text-brand-700"
                >
                  + Add Component
                </button>
              </div>
              {componentVersions.length > 0 && (
                <div className="space-y-2 border border-gray-200 rounded-md p-3 bg-gray-50">
                  {componentVersions.map((cv, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={cv.identifier}
                        onChange={(e) =>
                          updateComponentVersion(
                            index,
                            "identifier",
                            e.target.value,
                          )
                        }
                        className="input flex-1"
                        placeholder="Component identifier (e.g., api-server)"
                      />
                      <input
                        type="text"
                        value={cv.version}
                        onChange={(e) =>
                          updateComponentVersion(
                            index,
                            "version",
                            e.target.value,
                          )
                        }
                        className="input w-32"
                        placeholder="Version"
                      />
                      <button
                        type="button"
                        onClick={() => removeComponentVersion(index)}
                        className="p-1 text-gray-400 hover:text-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Track which component versions were deployed. This updates
                expected versions for drift detection.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={createMutation.isPending}
              >
                {createMutation.isPending
                  ? "Recording..."
                  : "Record Deployment"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="mt-6 flex gap-4">
        <select
          value={filters.service}
          onChange={(e) => setFilters({ ...filters, service: e.target.value })}
          className="input w-48"
        >
          <option value="">All Services</option>
          {(services?.services ?? []).map((svc) => (
            <option key={svc.id} value={svc.slug}>
              {svc.name}
            </option>
          ))}
        </select>

        <EnvironmentSelect
          value={filters.environment}
          onChange={(value) => setFilters({ ...filters, environment: value })}
          className="input w-48"
          valueKey="slug"
        />

        <select
          value={filters.group}
          onChange={(e) => {
            const value = e.target.value;
            setFilters({ ...filters, group: value });
            if (value) {
              setSearchParams((prev) => {
                prev.set("group", value);
                return prev;
              });
            } else {
              setSearchParams((prev) => {
                prev.delete("group");
                return prev;
              });
            }
          }}
          className="input w-48"
        >
          <option value="">All Groups</option>
          {groups.map((group) => (
            <option key={group.id} value={group.slug}>
              {group.name}
            </option>
          ))}
        </select>
      </div>

      {/* Deployments Table */}
      <div className="mt-4 card overflow-hidden p-0">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Environment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Deployed By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  When
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Link
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(deployments?.data ?? []).map((deployment) => (
                <tr
                  key={deployment.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => setSelectedDeploymentId(deployment.id)}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {titleCase(deployment.service_name)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className="text-gray-900">{deployment.version}</span>
                    {deployment.previous_version && (
                      <span className="text-gray-400 ml-1">
                        ← {deployment.previous_version}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {deployment.environment_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {deployment.deployed_by}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        deployment.deployment_method === "ci_cd"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {deployment.deployment_method === "ci_cd"
                        ? "CI/CD"
                        : "Manual"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        deployment.status === "failed"
                          ? "bg-red-100 text-red-800"
                          : "bg-green-100 text-green-800"
                      }`}
                    >
                      {deployment.status === "failed" ? "Failed" : "Success"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(deployment.deployed_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {deployment.ci_cd_job_url && (
                      <a
                        href={deployment.ci_cd_job_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-600 hover:text-brand-700"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </td>
                </tr>
              ))}

              {(deployments?.data ?? []).length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-6 py-8 text-center text-sm text-gray-500"
                  >
                    No deployments found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination info */}
      {deployments && deployments.pagination.total > 0 && (
        <div className="mt-4 text-sm text-gray-500">
          Showing {(deployments.data ?? []).length} of{" "}
          {deployments.pagination.total} deployments
        </div>
      )}

      {/* API Examples */}
      <div className="mt-6 border border-gray-200 rounded-lg bg-white">
        <button
          onClick={() => setShowApiExamples(!showApiExamples)}
          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
        >
          <div className="flex items-center gap-2">
            <Code className="h-4 w-4 text-gray-500" />
            <span className="font-medium text-gray-700">API Examples</span>
          </div>
          {showApiExamples ? (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-400" />
          )}
        </button>

        {showApiExamples && (
          <div className="px-4 pb-4 space-y-6 border-t border-gray-200">
            {/* Check Approval Status */}
            <div className="mt-4">
              <h4 className="font-medium text-gray-900 mb-2">
                1. Check Approval Status (before deploying)
              </h4>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
                {`curl -X GET "http://localhost:8080/api/v1/deployment-requests/{environment}/{service}/{version}/status" \\
  -H "X-API-Key: dpo_YOUR_API_KEY"

# Example:
curl -X GET "http://localhost:8080/api/v1/deployment-requests/production/api-service/2.5.1/status" \\
  -H "X-API-Key: dpo_YOUR_API_KEY"`}
              </pre>
              <p className="mt-2 text-sm text-gray-500">
                Returns{" "}
                <code className="bg-gray-100 px-1 rounded">
                  {"can_proceed: true"}
                </code>{" "}
                if deployment is approved. For environments that don't require
                approval, this always returns approved.
              </p>
            </div>

            {/* Successful Deployment */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">
                2. Record Successful Deployment
              </h4>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
                {`curl -X POST http://localhost:8080/api/v1/deployments \\
  -H "X-API-Key: dpo_YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "service": "api-service",
    "version": "2.5.1",
    "environment": "production",
    "deployed_by": "jenkins",
    "status": "success",
    "deployment_method": "ci_cd",
    "ci_cd_job_url": "https://jenkins.example.com/job/123",
    "notes": "Release 2.5.1 with auth fixes",
    "component_versions": {
      "api-server": "2.5.1",
      "worker": "2.5.1"
    }
  }'`}
              </pre>
              <p className="mt-2 text-sm text-gray-500">
                Automatically links to the matching DeploymentRequest by
                environment/service/version and marks it as "completed".
              </p>
            </div>

            {/* Failed Deployment */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">
                3. Record Failed Deployment
              </h4>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
                {`curl -X POST http://localhost:8080/api/v1/deployments \\
  -H "X-API-Key: dpo_YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "service": "api-service",
    "version": "2.5.1",
    "environment": "production",
    "deployed_by": "jenkins",
    "status": "failed",
    "deployment_method": "ci_cd",
    "ci_cd_job_url": "https://jenkins.example.com/job/123",
    "notes": "Deployment failed - rolled back"
  }'`}
              </pre>
              <p className="mt-2 text-sm text-gray-500">
                Failed deployments keep the DeploymentRequest in "approved"
                status so it can be retried.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedDeploymentId && (
        <DeploymentDetailsModal
          deploymentId={selectedDeploymentId}
          onClose={() => setSelectedDeploymentId(null)}
        />
      )}
    </div>
  );
}
